import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { trackLead } from "@/lib/tracking";
import { captureAttribution, getAttributionPayload } from "@/lib/attribution";

/**
 * Ad-specific lead capture page. One generic component, many routes.
 * Slug comes from `/:slug` in the URL (`/start`, `/goa`, `/thailand`, ...).
 *
 * Fetches landing data from `/api/landings/{slug}`. If not found / inactive,
 * silently redirects to the home page so the URL is never a dead end.
 */
export default function LeadCapture() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [landing, setLanding] = useState(null);
  const [loadError, setLoadError] = useState(false);

  // Capture UTMs as soon as the component mounts so they're stored even if the
  // visitor never submits (also persists across same-session navigation).
  useEffect(() => { captureAttribution(); }, []);

  useEffect(() => {
    let cancelled = false;
    api.get(`/landings/${encodeURIComponent(slug || "")}`)
      .then((res) => { if (!cancelled) setLanding(res.data); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [slug]);

  // No-index meta + lightweight SEO
  useEffect(() => {
    if (!landing) return undefined;
    const prevTitle = document.title;
    if (landing.meta?.title) document.title = landing.meta.title;

    // Robots meta — landing pages should not be indexed by default
    let robots = document.querySelector('meta[name="robots"]');
    let createdRobots = false;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
      createdRobots = true;
    }
    const prevRobotsContent = robots.getAttribute("content");
    robots.setAttribute("content", landing.noindex !== false ? "noindex, nofollow" : "index, follow");

    return () => {
      document.title = prevTitle;
      if (createdRobots) robots.remove();
      else if (prevRobotsContent) robots.setAttribute("content", prevRobotsContent);
    };
  }, [landing]);

  // No landing in DB → bounce to home so paid traffic never lands on a 404
  useEffect(() => {
    if (loadError) navigate("/", { replace: true });
  }, [loadError, navigate]);

  if (!landing) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center text-charcoal/40 font-display text-2xl">
        <Loader2 className="h-6 w-6 animate-spin mr-3" /> Loading&hellip;
      </div>
    );
  }
  return <LeadCaptureView landing={landing} />;
}

function LeadCaptureView({ landing }) {
  const labels = landing.form_labels || {};
  const destinations = landing.destinations || [];

  const [form, setForm] = useState({ wedding_location: "", name: "", phone: "", wedding_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const redirectTimer = useRef(null);

  const heroStyle = useMemo(() => {
    if (!landing.background_image_url) return undefined;
    return { backgroundImage: `linear-gradient(rgba(34,34,34,0.55), rgba(34,34,34,0.78)), url(${landing.background_image_url})` };
  }, [landing.background_image_url]);

  useEffect(() => () => { if (redirectTimer.current) clearTimeout(redirectTimer.current); }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.wedding_location) {
      toast.error("Please fill in name, mobile and destination.");
      return;
    }
    setSubmitting(true);
    try {
      const attribution = getAttributionPayload();
      const payload = {
        ...form,
        source: "landing_page",
        landing_slug: landing.slug,
        ...attribution,
      };
      await api.post("/leads", payload);
      trackLead(payload); // Meta `Lead` + GA4 `generate_lead`
      setDone(true);

      const delay = Math.max(1000, Number(landing.redirect_delay_ms) || 2000);
      const target = landing.redirect_url || "/";
      redirectTimer.current = setTimeout(() => {
        // Use window.location for an external/absolute path; navigate for in-app
        if (/^https?:\/\//i.test(target)) {
          window.location.href = target;
        } else {
          window.location.href = target;
        }
      }, delay);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not submit. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid={`landing-${landing.slug}`}
      className="min-h-screen bg-ivory text-charcoal flex flex-col"
      style={heroStyle ? { ...heroStyle, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
    >
      {/* Header logo */}
      <header className="flex-shrink-0 px-5 md:px-10 pt-7 md:pt-9 pb-2 md:pb-4">
        {landing.logo_url ? (
          <img src={landing.logo_url} alt="Weddings by Mostash" className="h-14 md:h-16 w-auto" />
        ) : (
          <div className="font-display text-xl md:text-2xl tracking-tight text-charcoal">
            <span className="italic font-light">Weddings by</span>{" "}<span className="font-medium">Mostash</span>
          </div>
        )}
      </header>

      <section className="flex-1 px-5 md:px-10 pb-10 md:pb-14">
        <div className="max-w-xl mx-auto pt-6 md:pt-10">
          {done ? (
            <ThankYou title={landing.success_title || "Thank you."} message={landing.success_message} />
          ) : (
            <>
              <div className="text-center mb-7 md:mb-9">
                <div className="uppercase tracking-[0.3em] text-[10px] md:text-[11px] text-teal-600 font-medium mb-4">
                  {landing.label}
                </div>
                <h1
                  data-testid="landing-headline"
                  className="font-display text-[2.1rem] leading-[1.1] sm:text-[2.6rem] md:text-5xl tracking-tight text-charcoal"
                >
                  {landing.headline}
                </h1>
                {landing.subheadline ? (
                  <p className="mt-4 text-charcoal/65 font-light text-base md:text-lg">{landing.subheadline}</p>
                ) : null}
              </div>

              {/* Trust strip */}
              {Array.isArray(landing.trust_strip) && landing.trust_strip.length > 0 ? (
                <div className="grid grid-cols-3 gap-3 md:gap-6 mb-7 md:mb-10 text-center border-y border-beige py-5">
                  {landing.trust_strip.map((t, i) => (
                    <div key={i}>
                      <div className="font-display text-base md:text-lg text-charcoal leading-tight">{t.value}</div>
                      <div className="uppercase tracking-[0.22em] text-[9px] md:text-[10px] text-charcoal/55 mt-1.5">{t.label}</div>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* Form */}
              <form onSubmit={submit} data-testid="landing-form" className="bg-white border border-beige p-5 md:p-7 space-y-4">
                <SelectField
                  label={labels.destination || "Which city are you planning your wedding?"}
                  name="wedding_location"
                  value={form.wedding_location}
                  onChange={onChange}
                  options={destinations}
                  testid="landing-input-destination"
                />
                <TextField
                  label={labels.name || "Full Name"}
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  testid="landing-input-name"
                  autoComplete="name"
                />
                <TextField
                  label={labels.phone || "Mobile Number"}
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={onChange}
                  testid="landing-input-phone"
                  autoComplete="tel"
                />
                <TextField
                  label={labels.wedding_date || "When is your celebration?"}
                  name="wedding_date"
                  type="date"
                  value={form.wedding_date}
                  onChange={onChange}
                  testid="landing-input-date"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="landing-submit"
                  className="group mt-6 w-full inline-flex items-center justify-center gap-2 h-14 md:h-13 bg-teal-600 text-ivory text-[12px] md:text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-charcoal transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending&hellip;</> : <>{labels.button || "Get Personalised Consultation"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
                </button>

                <div className="flex items-center justify-center gap-1.5 text-[10px] text-charcoal/45 uppercase tracking-[0.22em] mt-3">
                  <ShieldCheck className="h-3 w-3" /> Your information is private and secure.
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function ThankYou({ title, message }) {
  return (
    <div className="text-center bg-white border border-beige p-8 md:p-10" data-testid="landing-success">
      <div className="inline-flex h-14 w-14 items-center justify-center bg-teal-600/10 text-teal-600 mb-5">
        <ShieldCheck className="h-7 w-7" strokeWidth={1.4} />
      </div>
      <div className="uppercase tracking-[0.3em] text-[10px] text-teal-600 mb-3">Inquiry received</div>
      <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-4">{title}</h2>
      <p className="text-charcoal/70 font-light leading-relaxed max-w-md mx-auto">{message}</p>
      <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-charcoal/45">
        <Loader2 className="h-3 w-3 animate-spin" /> Redirecting&hellip;
      </div>
    </div>
  );
}

function TextField({ label, name, value, onChange, type = "text", inputMode, testid, autoComplete }) {
  return (
    <label className="block">
      <span className="uppercase tracking-[0.25em] text-[10px] text-charcoal/55">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        inputMode={inputMode}
        autoComplete={autoComplete}
        data-testid={testid}
        required={name !== "wedding_date"}
        className="mt-2 w-full h-12 bg-ivory border border-beige focus:border-teal-600 outline-none px-3.5 text-base text-charcoal font-light"
      />
    </label>
  );
}

function SelectField({ label, name, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="uppercase tracking-[0.25em] text-[10px] text-charcoal/55">{label}</span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required
        data-testid={testid}
        className="mt-2 w-full h-12 bg-ivory border border-beige focus:border-teal-600 outline-none px-3 text-base text-charcoal font-light appearance-none"
      >
        <option value="">Choose one&hellip;</option>
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </label>
  );
}
